import { Trans } from '@lingui/react/macro'
import { Anchor, Box, Group, Stack } from '@mantine/core'
import { formatShortDate } from '@markku/utils'
import { IconCalendarEvent, IconHistory } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { ServiceLabel } from './ServiceLabel'
import { StatusCell } from './StatusCell'
import { useServiceDisplay } from './useServiceDisplay'
import type { Service } from './utils'

export function ServiceCard({
  service,
  companyId,
}: {
  service: Service
  companyId: string
}) {
  const { meta, statusLabel, openVisit, hasLast, hasNext } = useServiceDisplay(
    service,
    companyId,
  )

  return (
    <Stack
      gap="xs"
      py="sm"
      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
        <ServiceLabel service={service} color={meta.color} />
        <Box style={{ flexShrink: 0 }}>
          <StatusCell
            service={service}
            statusLabel={statusLabel}
            color={meta.color}
            size="xs"
            fw={700}
            align="right"
          />
        </Box>
      </Group>

      {(hasLast || hasNext) && (
        <Stack gap={4} pl={22}>
          {hasLast && (
            <VisitLink
              icon={
                <IconHistory size={15} color="var(--mantine-color-gray-6)" />
              }
              label={<Trans>Last visit</Trans>}
              date={formatShortDate(service.lastCompletedDate!)}
              staff={service.lastVisitStaff.join(', ')}
              onClick={() => openVisit(service.lastCompletedVisitId!)}
            />
          )}
          {hasNext && (
            <VisitLink
              icon={
                <IconCalendarEvent
                  size={15}
                  color="var(--mantine-color-gray-6)"
                />
              }
              label={<Trans>Next visit</Trans>}
              date={formatShortDate(service.nextScheduledDate!)}
              staff={service.nextVisitStaff.join(', ')}
              onClick={() => openVisit(service.nextScheduledVisitId!)}
            />
          )}
        </Stack>
      )}
    </Stack>
  )
}

function VisitLink({
  icon,
  label,
  date,
  staff,
  onClick,
}: {
  icon: ReactNode
  label: ReactNode
  date: string
  staff: string
  onClick: () => void
}) {
  return (
    <Group gap={6} wrap="nowrap" align="center" style={{ paddingBlock: 2 }}>
      {icon}
      <Anchor
        component="button"
        type="button"
        underline="always"
        c="brandBrown.7"
        fw={600}
        size="sm"
        ta="left"
        onClick={onClick}
      >
        {label} · {date}
        {staff ? ` · ${staff}` : ''}
      </Anchor>
    </Group>
  )
}
